/**
 * Core Tracking SDK implementation.
 *
 * Dual-routes events: pushes to GTM dataLayer (for GA4 tags)
 * AND calls PostHog SDK directly (for immediate client-side features).
 *
 * Consent is enforced at the GTM layer (tags don't fire without consent).
 * The PostHog SDK handles its own opt-in/opt-out state.
 */

import posthog from 'posthog-js';
import type { AnalyticsEvent } from './events';
import { EVENT_ROUTING } from './events';
import type { TrackingSDK, UserTraits, GroupTraits } from './types';
import { getGlobalProperties } from './properties';
import { pushToDataLayer } from './gtm';
import { getAnalyticsConfig } from './config';
import { safeLog } from '@/lib/safeLog';
import { brazeLogEvent, brazeSetAttribute, brazeSetEmail } from './braze';

function shouldRouteToGA4(eventName: string): boolean {
  const routing = EVENT_ROUTING[eventName as keyof typeof EVENT_ROUTING];
  return routing === 'ga4' || routing === 'both' || routing === 'all';
}

function shouldRouteToPostHog(eventName: string): boolean {
  const routing = EVENT_ROUTING[eventName as keyof typeof EVENT_ROUTING];
  return routing !== 'ga4' && routing !== 'braze';
}

function shouldRouteToBraze(eventName: string): boolean {
  const routing = EVENT_ROUTING[eventName as keyof typeof EVENT_ROUTING];
  return routing === 'braze' || routing === 'posthog_braze' || routing === 'all';
}

export const analytics: TrackingSDK = {
  trackEvent(event: AnalyticsEvent) {
    const globalProps = getGlobalProperties();
    const enrichedProps = {
      ...globalProps,
      ...event.properties,
    };

    if (getAnalyticsConfig().environment === 'development') {
      safeLog('[Analytics]', event.name, event.properties);
    }

    if (shouldRouteToGA4(event.name)) {
      pushToDataLayer(event.name, enrichedProps);
    }

    if (shouldRouteToPostHog(event.name)) {
      posthog.capture(event.name, enrichedProps);
    }

    if (shouldRouteToBraze(event.name)) {
      brazeLogEvent(
        event.name,
        event.properties as Record<string, string | number | boolean>
      );
    }
  },

  identify(userId: string, traits?: UserTraits) {
    // PII traits (email, etc.) stay in PostHog only; only non-PII goes to dataLayer
    posthog.identify(userId, traits);

    // Sync email to the Braze User object (not a custom attribute)
    if (traits?.email) {
      brazeSetEmail(traits.email);
    }

    pushToDataLayer('user_identified', {
      user_id: userId,
    });
  },

  setGroup(groupType: 'company', groupKey: string, traits?: GroupTraits) {
    posthog.group(groupType, groupKey, traits);
  },

  setUserProperties(props: Partial<UserTraits>) {
    // All user properties stay in PostHog; dataLayer only gets non-PII fields
    posthog.setPersonProperties(props);

    const safePropKeys = ['plan_type', 'country', 'user_origin'];
    const safeProps: Record<string, unknown> = {};
    for (const key of safePropKeys) {
      if (key in props) safeProps[key] = props[key as keyof UserTraits];
    }
    if (Object.keys(safeProps).length > 0) {
      pushToDataLayer('user_properties_updated', { user_properties: safeProps });
    }
  },

  getFeatureFlag(key: string): string | boolean | undefined {
    return posthog.getFeatureFlag(key) ?? undefined;
  },

  reset() {
    posthog.reset();
    pushToDataLayer('user_logout', {});
  },

  setBrazeAttribute(key: string, value: string | number | boolean | Date | null) {
    brazeSetAttribute(key, value);
  },
};
